import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getSharedMVVForUser } from '../services/firebase/missionVisionValues';
import {
  fetchHRTemplates,
  countAvailableTemplates,
  getHandbookDocument
} from '../utils/hrTemplates';
import { fetchHRStructure } from '../services/hrStructureService';
import { DEFAULT_HR_SECTIONS } from '../utils/hrMaterials';

const EMPTY_METRIC = { value: '—', rawValue: null, status: 'none', source: 'manual', sub: null, cue: null };

const DEFAULT_MISSION = 'To tangibly improve the lives of our clients and employees.';
const DEFAULT_VISION =
  'To grow a legacy of quality, teamwork, and innovation that reshapes the remodeling experience.';

const ACTIVE_STATUSES = ['Requested', 'Pending', 'In Progress'];

const truncate = (text, maxLen = 120) => {
  if (!text || typeof text !== 'string') return '';
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen).trim()}…`;
};

const isMvvCustomized = (mvvData) => {
  if (!mvvData) return false;
  const mission = (mvvData.mission || '').trim();
  const vision = (mvvData.vision || '').trim();
  if (mission && mission !== DEFAULT_MISSION) return true;
  if (vision && vision !== DEFAULT_VISION) return true;
  const values = Array.isArray(mvvData.values) ? mvvData.values : [];
  const defaultValues = [
    { title: 'Customer Focus', description: 'We put our customers at the center of everything we do.' },
    { title: 'Innovation', description: 'We constantly seek new ways to improve and evolve.' },
    { title: 'Integrity', description: 'We operate with honesty, transparency, and ethical standards.' },
    { title: 'Excellence', description: 'We strive for the highest quality in all our endeavors.' }
  ];
  if (values.length !== defaultValues.length) return true;
  return values.some((v, i) => {
    const d = defaultValues[i];
    return !d || v.title !== d.title || v.description !== d.description;
  });
};

const sortPositionsByDate = (positions) =>
  [...positions].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

// userId lets an internal viewer (CRM Portal View) load the HR dashboard for a
// specific client instead of the logged-in user.
export const useHRDashboard = ({ userId = null } = {}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reloadIndex, setReloadIndex] = useState(0);
  const refresh = () => setReloadIndex((i) => i + 1);
  const [state, setState] = useState({
    asOfDate: new Date(),
    metrics: {
      activePositions: EMPTY_METRIC,
      inProgress: EMPTY_METRIC,
      filled: EMPTY_METRIC,
      jobListings: EMPTY_METRIC,
      mvv: EMPTY_METRIC,
      templates: EMPTY_METRIC
    },
    documents: [],
    handbook: null
  });

  const targetUid = userId || currentUser?.uid || null;

  useEffect(() => {
    if (!targetUid) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      const uid = targetUid;
      // Email fallback for MVV only applies to the logged-in user.
      const mvvEmail = userId ? null : currentUser?.email;

      try {
        const [positionsSnap, jobListingsSnap, userDocRes, mvvResult, templateDocs, hrSections] = await Promise.all([
          getDocs(query(collection(db, 'positions'), where('userId', '==', uid))),
          getDocs(collection(db, 'jobListings')),
          getDoc(doc(db, 'users', uid)),
          getSharedMVVForUser(uid, mvvEmail).catch(() => ({ data: null })),
          fetchHRTemplates().catch(() => []),
          fetchHRStructure().catch(() => DEFAULT_HR_SECTIONS)
        ]);

        if (cancelled) return;

        const positions = positionsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const active = positions.filter((p) => ACTIVE_STATUSES.includes(p.status));
        const requested = positions.filter((p) => p.status === 'Requested');
        const pending = positions.filter((p) => p.status === 'Pending');
        const inProgress = positions.filter((p) => p.status === 'In Progress');
        const filled = positions.filter((p) => p.status === 'Completed');
        const recentActive = sortPositionsByDate(active).slice(0, 3);
        const statusCounts = {
          requested: requested.length,
          pending: pending.length,
          inProgress: inProgress.length,
          filled: filled.length,
        };

        const jobListings = jobListingsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((j) => j.status !== 'deleted');
        const activeListings = jobListings.filter(
          (j) => (j.status || '').toLowerCase() === 'active'
        );

        const userDoc = userDocRes.exists() ? userDocRes.data() : null;
        const mvvData = mvvResult?.data || null;
        const mvvCompleted =
          userDoc?.businessAssessment?.mvvCompleted === true || isMvvCustomized(mvvData);
        const values = Array.isArray(mvvData?.values) ? mvvData.values : [];

        const templateTitles = hrSections.flatMap((s) => (s.items || []).map((i) => i.title));
        const { available, total } = countAvailableTemplates(templateDocs, templateTitles);
        const handbook = getHandbookDocument(templateDocs);

        const templateStatus =
          available === total ? 'good' : available >= total / 2 ? 'ok' : available > 0 ? 'poor' : 'none';

        const metrics = {
          activePositions: {
            value: String(active.length),
            rawValue: active.length,
            status: active.length > 0 ? 'ok' : 'none',
            source: 'manual',
            sub: active.length > 0 ? `${active.length} open role${active.length === 1 ? '' : 's'}` : 'No active searches',
            cue: active.length > 0 ? 'Track progress in the hiring hub.' : 'Request your first position search to get started.',
            recentPositions: recentActive,
            statusCounts,
            totalPositions: positions.length
          },
          inProgress: {
            value: String(inProgress.length),
            rawValue: inProgress.length,
            status: inProgress.length > 0 ? 'ok' : 'none',
            source: 'manual',
            sub: inProgress.length > 0 ? 'Currently being filled' : 'None in progress',
            cue: null
          },
          filled: {
            value: String(filled.length),
            rawValue: filled.length,
            status: filled.length > 0 ? 'good' : 'none',
            source: 'manual',
            sub: filled.length > 0 ? 'Successfully completed' : 'No filled positions yet',
            cue: null
          },
          jobListings: {
            value: String(activeListings.length),
            rawValue: activeListings.length,
            status: activeListings.length > 0 ? 'ok' : 'none',
            source: 'manual',
            sub:
              activeListings.length > 0
                ? `${activeListings.length} active listing${activeListings.length === 1 ? '' : 's'}`
                : 'Create a job listing template',
            cue: null
          },
          mvv: {
            value: mvvCompleted ? 'Complete' : 'In Progress',
            rawValue: mvvCompleted ? 1 : 0,
            status: mvvCompleted ? 'good' : 'poor',
            source: 'manual',
            sub: mvvCompleted ? `${values.length} core value${values.length === 1 ? '' : 's'} defined` : 'Define your company direction',
            cue: mvvCompleted ? null : 'Mission, vision, and values guide hiring and culture.',
            completed: mvvCompleted,
            mission: truncate(mvvData?.mission || ''),
            vision: truncate(mvvData?.vision || ''),
            values,
            valuesCount: values.length
          },
          templates: {
            value: `${available}/${total}`,
            rawValue: available,
            status: templateStatus,
            source: 'manual',
            sub: `${available} of ${total} templates ready to download`,
            cue: available < total ? 'Some templates are still being prepared by your HR team.' : 'All HR templates are available.',
            available,
            total,
            documents: templateDocs
          }
        };

        setState({
          asOfDate: new Date(),
          metrics,
          documents: templateDocs,
          handbook
        });
      } catch (error) {
        console.error('Error loading HR dashboard:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [targetUid, currentUser?.email, userId, reloadIndex]);

  return {
    loading,
    refresh,
    asOfDate: state.asOfDate,
    metrics: state.metrics,
    documents: state.documents,
    handbook: state.handbook
  };
};

export default useHRDashboard;
